import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Module-level option lists — stable references.
const ORG_TYPES = [
  { value: 'sme', label: 'SME' },
  { value: 'accounting_firm', label: 'Accounting Firm' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'government', label: 'Government Agency' },
  { value: 'university', label: 'University' },
  { value: 'other', label: 'Other' },
];

const INVOLVEMENT_LEVELS = [
  { value: 'direct', label: 'Direct / Hands-on' },
  { value: 'supervisory', label: 'Supervisory' },
  { value: 'managerial', label: 'Managerial' },
];

// Industry list — used to guide the Professional Report's context routing
// without hardcoding the output to a single sector.
const INDUSTRIES = [
  { value: 'Education / University',          label: 'Education / University' },
  { value: 'Professional Services',           label: 'Professional Services' },
  { value: 'Accounting / Tax',                label: 'Accounting / Tax' },
  { value: 'Manufacturing',                   label: 'Manufacturing' },
  { value: 'Retail',                          label: 'Retail' },
  { value: 'Healthcare',                      label: 'Healthcare' },
  { value: 'Logistics / Transportation',      label: 'Logistics / Transportation' },
  { value: 'Construction',                    label: 'Construction' },
  { value: 'Hospitality / Tourism',           label: 'Hospitality / Tourism' },
  { value: 'Government / Public Sector',      label: 'Government / Public Sector' },
  { value: 'Financial Services / Banking',    label: 'Financial Services / Banking' },
  { value: 'IT / Technology',                 label: 'IT / Technology' },
  { value: 'Training / Consultancy',          label: 'Training / Consultancy' },
  { value: 'Wholesale / Distribution',        label: 'Wholesale / Distribution' },
  { value: 'Property / Real Estate',          label: 'Property / Real Estate' },
  { value: 'Agriculture / Plantation',        label: 'Agriculture / Plantation' },
  { value: 'NGO / Non-Profit',                label: 'NGO / Non-Profit' },
  { value: 'Other',                           label: 'Other' },
];

// Primary workflow / function area — additional contextual signal.
const PRIMARY_FUNCTIONS = [
  { value: 'Operations',               label: 'Operations' },
  { value: 'Finance',                  label: 'Finance' },
  { value: 'HR',                       label: 'HR' },
  { value: 'Procurement',              label: 'Procurement' },
  { value: 'Sales',                    label: 'Sales' },
  { value: 'Customer Service',         label: 'Customer Service' },
  { value: 'Academic Administration',  label: 'Academic Administration' },
  { value: 'Compliance',               label: 'Compliance' },
  { value: 'IT Support',               label: 'IT Support' },
  { value: 'Project Management',       label: 'Project Management' },
  { value: 'Other',                    label: 'Other' },
];

const TextField = ({ id, label, value, onChange, testId, type = 'text', placeholder }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      type={type}
      data-testid={testId}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
  </div>
);

const SelectField = ({ id, label, value, onValueChange, placeholder, options, testId }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id={id} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const IdentitySection = ({ profile, setText }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <TextField id="name" label="Full Name *" testId="profile-name-input"
        value={profile.name} onChange={setText('name')} placeholder="John Doe" />
      <TextField id="email" label="Email *" testId="profile-email-input" type="email"
        value={profile.email} onChange={setText('email')} placeholder="john@company.com" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <TextField id="phone" label="Phone / WhatsApp *" testId="profile-phone-input"
        value={profile.phone} onChange={setText('phone')} placeholder="+60123456789" />
      <TextField id="organization" label="Organization Name *" testId="profile-organization-input"
        value={profile.organization_name} onChange={setText('organization_name')} placeholder="Company Name" />
    </div>
  </>
);

const RoleSection = ({ profile, setText, setSelect }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <TextField id="position" label="Position"
        value={profile.position} onChange={setText('position')} placeholder="Manager, Accountant, etc." />
      <TextField id="department" label="Department / Unit"
        value={profile.department} onChange={setText('department')} placeholder="Finance, Operations, etc." />
    </div>
    <SelectField id="org-type" label="Type of Organization"
      value={profile.organization_type} onValueChange={setSelect('organization_type')}
      placeholder="Select organization type" options={ORG_TYPES} />
  </>
);

const ExperienceSection = ({ profile, setSelect, setYears }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      <Label htmlFor="years">Years of Work Experience</Label>
      <Input id="years" type="number" value={profile.years_experience} onChange={setYears} min="0" />
    </div>
    <SelectField id="involvement" label="Level of Involvement"
      value={profile.involvement_level} onValueChange={setSelect('involvement_level')}
      placeholder="Select involvement level" options={INVOLVEMENT_LEVELS} />
  </div>
);

const SectorSection = ({ profile, setText, setSelect }) => {
  const showOther = (profile.industry || '').toLowerCase() === 'other';
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-6">
        <SelectField id="industry" label="Industry / Sector"
          testId="profile-industry-select"
          value={profile.industry} onValueChange={setSelect('industry')}
          placeholder="Select your industry" options={INDUSTRIES} />
        {showOther && (
          <TextField id="industry-other" label="Please specify your industry"
            testId="profile-industry-other-input"
            value={profile.industry_other} onChange={setText('industry_other')}
            placeholder="e.g. Yacht charter operations" />
        )}
      </div>
      <SelectField id="primary-function" label="Primary Workflow Area / Function"
        testId="profile-primary-function-select"
        value={profile.primary_function} onValueChange={setSelect('primary_function')}
        placeholder="Select the primary function" options={PRIMARY_FUNCTIONS} />
    </div>
  );
};

const ProfileStep = ({ profile, onChange }) => {
  // Inline handlers — children are unmemoized, so referential equality doesn't matter.
  // Avoids useCallback-related exhaustive-deps surface.
  const setField = (field, value) => onChange({ ...profile, [field]: value });
  const setText = (field) => (e) => setField(field, e.target.value);
  const setSelect = (field) => (value) => setField(field, value);
  const setYears = (e) => setField('years_experience', parseInt(e.target.value, 10) || 0);

  return (
    <div data-testid="profile-step">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Respondent &amp; Organisation Profile
        </h2>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">
          Tell us about yourself and your organisation. We use this to personalise your report.
        </p>
      </div>
      <div className="space-y-6">
        <IdentitySection profile={profile} setText={setText} />
        <RoleSection profile={profile} setText={setText} setSelect={setSelect} />
        <ExperienceSection profile={profile} setSelect={setSelect} setYears={setYears} />
        <SectorSection profile={profile} setText={setText} setSelect={setSelect} />
        <div>
          <Label htmlFor="problem">Main Workflow Problem Faced</Label>
          <Textarea id="problem" data-testid="profile-problem-input"
            value={profile.main_problem} onChange={setText('main_problem')}
            placeholder="Describe the main workflow challenges your organization faces" rows={4} />
        </div>
      </div>
    </div>
  );
};

export default ProfileStep;
